import React, { useMemo } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { useTenantContext } from '@/context'
import { DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import { getTenantConfigurationOptionValue } from '@/utils/tenant-configuration-option'
import { DEFAULT_BANNER_HEADING, DEFAULT_BANNER_MESSAGE } from './constants'

/**
 * The configured values arrive from a rich text editor, so each line is wrapped
 * in a <p> carrying the browser's default 1em margins - which is why a
 * configured banner stood apart while the plain-string fallbacks sat tight.
 * Paragraphs are flattened to the line they are, with a small gap kept between
 * consecutive ones in case somebody writes two.
 */
const RICH_TEXT_SX = {
  '& p': { m: 0 },
  '& p + p': { mt: '0.4em' },
}

/**
 * AC2. The heading and message come from System Configurations, through the
 * same tenant_configuration_option rail as the other donor portal screen
 * messages. A blood center that has written nothing gets the default copy
 * rather than an empty banner.
 */
export const RecognitionBanner = () => {
  const { tenant } = useTenantContext()

  const [heading, message] = useMemo(() => {
    const read = code =>
      (
        getTenantConfigurationOptionValue(
          tenant?.configuration_options,
          code
        ) || ''
      ).trim()
    return [
      read(DONOR_PORTAL_SCREEN_CONFIG_CODE.BADGES_BANNER_HEADING) ||
        DEFAULT_BANNER_HEADING,
      read(DONOR_PORTAL_SCREEN_CONFIG_CODE.BADGES_BANNER_MESSAGE) ||
        DEFAULT_BANNER_MESSAGE,
    ]
  }, [tenant?.configuration_options])

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 4,
        borderRadius: '10px',
        backgroundColor: 'secondary.main',
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'primary.main',
        }}
      >
        <StarRoundedIcon sx={{ color: '#fff', fontSize: 30 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {/* The editor is rich text, so the stored value may carry markup. */}
        <Typography
          variant='h6'
          fontWeight={700}
          sx={{ color: 'primary.main', ...RICH_TEXT_SX }}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
        <Typography
          variant='body1'
          sx={{ color: 'text.primary', ...RICH_TEXT_SX }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </Box>
    </Paper>
  )
}
